"use client";

import { useMemo } from "react";
import {
  keepPreviousData,
  useQuery,
  type QueryClient,
} from "@tanstack/react-query";

import { customInstance } from "@/lib/api/client";
import type { ListSequencesParams } from "@/lib/api/model/listSequencesParams";

import type { SequenceListItem } from "./use-sequences";

// `ListSequencesParams` is the job-scoped param shape; the project route
// accepts the same query params.

interface SequencesPayload {
  sequences: SequenceListItem[];
  next_cursor: string | null;
  total: number;
}

function buildUrl(projectId: string, params?: ListSequencesParams): string {
  const qs = new URLSearchParams();
  if (params?.include_archived) qs.append("include_archived", "true");
  if (params?.search) qs.append("search", params.search);
  if (params?.cursor) qs.append("cursor", params.cursor);
  if (params?.per_page != null) qs.append("per_page", String(params.per_page));
  const tail = qs.toString();
  return `/api/projects/${projectId}/sequences${tail ? `?${tail}` : ""}`;
}

interface UseProjectSequencesOptions {
  projectId: string | null | undefined;
  params?: ListSequencesParams;
}

/** Prefix for all project-scoped sequence queries (flat list, by-job, filter-jobs). */
export function projectSequencesQueryKey(
  projectId: string | null | undefined,
) {
  return ["projects", projectId, "sequences"] as const;
}

/** Invalidate sidebar tree + sheet lists after create / archive / rename. */
export function invalidateProjectSequences(
  queryClient: QueryClient,
  projectId: string | null | undefined,
) {
  if (!projectId) return;
  return queryClient.invalidateQueries({
    queryKey: projectSequencesQueryKey(projectId),
  });
}

/**
 * Project-scoped variant of {@link useSequences}. Hits the
 * ``GET /api/projects/{project_id}/sequences`` endpoint and returns the
 * same ``{ sequences, next_cursor, total }`` envelope.
 *
 * Hand-rolled over {@link customInstance} so callers don't have to wait
 * on an orval regen for the freshly-added route. Swap to the generated
 * hook once the OpenAPI spec is re-pulled.
 */
export function useProjectSequences({
  projectId,
  params,
}: UseProjectSequencesOptions) {
  const query = useQuery({
    queryKey: [...projectSequencesQueryKey(projectId), params],
    queryFn: () =>
      customInstance<SequencesPayload>(buildUrl(projectId!, params), {
        method: "GET",
      }),
    enabled: !!projectId,
    placeholderData: keepPreviousData,
  });

  const payload = useMemo<SequencesPayload>(
    () => ({
      sequences: query.data?.sequences ?? [],
      next_cursor: query.data?.next_cursor ?? null,
      total: query.data?.total ?? 0,
    }),
    [query.data],
  );

  return {
    sequences: payload.sequences,
    nextCursor: payload.next_cursor,
    total: payload.total ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPending: query.isPending,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
